import React, { useState, useEffect } from 'react';
import { Plus, ChevronDown, ChevronUp, Trash2, X } from 'lucide-react';
import {
    collection,
    doc,
    getDoc,
    getDocs,
    addDoc,
    updateDoc,
    deleteDoc,
    serverTimestamp,
    query,
    where,
    Timestamp
} from 'firebase/firestore';
import { db, auth, storage } from '../../src/firebaseConfig';
import { useSubAgentAuth } from '../pages/Clients/SubAgent/SubAgentAuthContext';
import { useParams } from 'react-router-dom';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { useAuth } from '../AuthContext';
import { useAppSelector } from '../redux/store';
import getCurrentScreen from '../Helper/GetScreen';

interface RegistrationFeeDetail {
    id: string;
    registrationFeeName: string;
    registrationFeeAmount: number;
}

interface InstallmentData {
    installmentNumber: number;
    dueDate: string;
    installmentAmount: number;
    paidAmount: number;
    receiptUrl: string;
    status: 'pending' | 'paid' | 'partial';
}

interface RegistrationData {
    id: number;
    firestoreId?: string; // Added to track Firestore document ID
    dateOfRegistration: string;
    registrationFee: number;
    discountAmount: number;
    afterDiscountAmount: number;
    paidAmount: number;
    receiptUrl: string;
    pendingAmount: number;
    registrationFeeStatus: string;
    feeDetailId: string;
    feeDetailName: string;
    paymentType?: 'fullPayment' | 'installment';
    numberOfInstallments?: number;
    installments?: InstallmentData[];
    referredPerson?: string;
    referralAmount?: number;
}

interface MultiRegistrationsProps {
    write: boolean;
}

const MultiRegistrations = ({ write }: MultiRegistrationsProps) => {

    const { leadId } = useParams();

    console.log(">>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>", leadId);

    const { currentSubAgent } = useSubAgentAuth();
    const { isAdmin, isManager, isStaff } = useAuth();

    const currentScreen = getCurrentScreen(useAppSelector(state => state.screen.currentScreen), isAdmin, isManager, isStaff);

    const [userData, setUserData] = useState<{ companyId: string } | null>(null);

    useEffect(() => {
        const fetchUserData = async () => {
            const currentUser = auth.currentUser;
            if (!currentUser) return;

            let userData = null;
            if (currentSubAgent) {
                userData = { companyId: currentSubAgent.companyId };
            } else {
                const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
                userData = { companyId: userDoc.data()?.companyId };
            }
            setUserData(userData);
        };

        fetchUserData();
    }, [currentSubAgent]);

    const [registrations, setRegistrations] = useState<RegistrationData[]>([]);
    const [expandedId, setExpandedId] = useState<number | null>(null);
    const [nextId, setNextId] = useState(1);
    const [dropdownStates, setDropdownStates] = useState<{ [key: number]: boolean }>({});
    const [showAddModal, setShowAddModal] = useState(false);
    const [availableFeeDetails, setAvailableFeeDetails] = useState<RegistrationFeeDetail[]>([]);
    const [selectedFeeDetail, setSelectedFeeDetail] = useState<RegistrationFeeDetail | null>(null);
    const [loadingFeeDetails, setLoadingFeeDetails] = useState(false);
    const [error, setError] = useState('');
    const [savingStates, setSavingStates] = useState<{ [key: number]: boolean }>({});
    const [deleteConfirmation, setDeleteConfirmation] = useState<{ show: boolean; registrationId: number | null }>({
        show: false,
        registrationId: null
    });

    // New state variables for payment options
    const [paymentType, setPaymentType] = useState<'fullPayment' | 'installment' | ''>('');
    const [numberOfInstallments, setNumberOfInstallments] = useState<number>(1);
    const [modalDiscountAmount, setModalDiscountAmount] = useState<number>(0);
    const [modalReferredPerson, setModalReferredPerson] = useState<string>('');
    const [modalReferralAmount, setModalReferralAmount] = useState<number>(0);

    // Installment modal state
    const [showInstallmentModal, setShowInstallmentModal] = useState(false);
    const [selectedRegistrationForInstallments, setSelectedRegistrationForInstallments] = useState<RegistrationData | null>(null);
    const [currentInstallmentIndex, setCurrentInstallmentIndex] = useState<number>(0);


    const [showFullPaymentModal, setShowFullPaymentModal] = useState(false);
    const [selectedRegistrationForFullPayment, setSelectedRegistrationForFullPayment] = useState<RegistrationData | null>(null);

    const [showReceiptUploadModal, setShowReceiptUploadModal] = useState(false);
    const [fullPaymentReceiptFile, setFullPaymentReceiptFile] = useState<File | null>(null);
    const [processingFullPayment, setProcessingFullPayment] = useState(false);
    const [registrationAfterFullPayment, setRegistrationAfterFullPayment] = useState<RegistrationData | null>(null);

    const [showConvertToInstallmentModal, setShowConvertToInstallmentModal] = useState(false);
    const [registrationForInstallmentConversion, setRegistrationForInstallmentConversion] = useState<RegistrationData | null>(null);
    const [newNumberOfInstallments, setNewNumberOfInstallments] = useState<number>(1);

    const statusOptions = ['payment pending', 'partial payment done', 'fully paid'];

    const sectionClass = "mb-4 bg-white rounded-lg shadow-sm border border-gray-300";
    const sectionHeaderClass = "text-lg font-semibold text-sky-950 mb-4 pb-2 border-b border-gray-200";
    const inputClass = "w-full py-2 px-4 border border-gray-500 rounded-3xl focus:outline-none focus:ring-2 focus:ring-blue-500";
    const labelClass = "block text-sm font-medium text-gray-700 mb-1";

    const fetchRegistrationFeeDetails = async () => {
        try {
            setLoadingFeeDetails(true);
            setError('');

            // Debug logging
            console.log('Company ID:', userData?.companyId);
            console.log('Full collection path:', `companies/${userData?.companyId}/registrationFeeDetails`);

            // Check if companyId exists
            if (!userData?.companyId) {
                setError('Company ID is missing');
                return;
            }

            const collectionPath = `companies/${userData.companyId}/registrationFeeDetails`;
            const registrationFeeStatusesSnapshot = await getDocs(
                collection(db, collectionPath)
            );

            console.log('Collection path used:', collectionPath);
            console.log('Found documents:', registrationFeeStatusesSnapshot.docs.length);

            // Log each document
            registrationFeeStatusesSnapshot.docs.forEach((doc, index) => {
                console.log(`Document ${index + 1} (ID: ${doc.id}):`, doc.data());
            });

            if (registrationFeeStatusesSnapshot.empty) {
                console.warn(`No data found in collection`);
                setError(`No records found. Please contact the administrator to add the required data.`);
                setAvailableFeeDetails([]);
                return;
            }

            const details = registrationFeeStatusesSnapshot.docs.map(doc => {
                const data = doc.data();
                console.log('Processing document:', doc.id, data);

                // Validate required fields
                if (!data.registrationFeeName || data.registrationFeeAmount === undefined) {
                    console.warn(`Document ${doc.id} is missing required fields:`, {
                        hasName: !!data.registrationFeeName,
                        hasAmount: data.registrationFeeAmount !== undefined,
                        data
                    });
                }

                return {
                    id: doc.id,
                    registrationFeeName: data.registrationFeeName || 'Unnamed Fee',
                    registrationFeeAmount: Number(data.registrationFeeAmount) || 0
                };
            });

            console.log('Processed registration fee details:', details);
            setAvailableFeeDetails(details);

        } catch (err) {
            console.error('Error fetching registration fee details:', err);
            const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred';
            setError(`Failed to load registration fee details: ${errorMessage}`);
        } finally {
            setLoadingFeeDetails(false);
        }
    };

    // Load existing registration form details from Firestore
    const loadExistingRegistrations = async () => {
        if (!userData?.companyId || !leadId) {
            console.log('Missing required data:', { companyId: userData?.companyId, leadId });
            return;
        }

        try {
            console.log('Loading registrations for:', {
                companyId: userData.companyId,
                leadId,
                currentUser: auth.currentUser?.uid,
                userType: { isAdmin, isManager, isStaff },
                currentScreen
            });

            const collectionPath = `companies/${userData.companyId}/registrationFormDetails`;

            let registrationQuery = query(
                collection(db, collectionPath),
                where("leadId", "==", leadId)
            );

            if (isAdmin && currentScreen === 'Admin') {
                // Admin can see all registrations for this lead
                registrationQuery = query(
                    collection(db, collectionPath),
                    where("leadId", "==", leadId)
                );
            } else if (isManager && currentScreen === 'Manager') {
                // Manager can see registrations from users under their management
                const userQuery = query(
                    collection(db, 'users'),
                    where('companyId', '==', userData.companyId),
                    where('isActive', '==', true),
                    where('managerIds', 'array-contains', auth.currentUser?.uid)
                );

                const userSnapshot = await getDocs(userQuery);
                const userIds = userSnapshot.docs.map(doc => doc.id);

                if (userIds.length > 0) {
                    // Include manager's own registrations plus team members' registrations
                    const allUserIds = [...userIds, auth.currentUser?.uid].filter(Boolean);
                    registrationQuery = query(
                        collection(db, collectionPath),
                        where("leadId", "==", leadId),
                        where("userId", "in", allUserIds)
                    );
                } else {
                    // If no team members, show only manager's own registrations
                    registrationQuery = query(
                        collection(db, collectionPath),
                        where("leadId", "==", leadId),
                        where("userId", "==", auth.currentUser?.uid)
                    );
                }
            } else if (isStaff && currentScreen === 'Staff') {
                const leadDoc = await getDoc(doc(db, `companies/${userData.companyId}/leads`, leadId));
                const leadData = leadDoc.data();
                const isCollaborator = leadData?.collaborators?.includes(auth.currentUser?.uid);
                const isAssignee = leadData?.assignedTo === auth.currentUser?.uid;

                if (isCollaborator || isAssignee) {
                    // If staff is collaborator or assignee, show all registrations for this lead
                    registrationQuery = query(
                        collection(db, collectionPath),
                        where("leadId", "==", leadId)
                    );
                } else {
                    // If not a collaborator or assignee, only see their own registrations
                    registrationQuery = query(
                        collection(db, collectionPath),
                        where("leadId", "==", leadId),
                        where("userId", "==", auth.currentUser?.uid)
                    );
                }
            } else {
                // Fallback: if user type is not clearly defined, show all registrations for this lead
                console.log('Using fallback query - showing all registrations for lead');
                registrationQuery = query(
                    collection(db, collectionPath),
                    where("leadId", "==", leadId)
                );
            }

            console.log('Final query will be executed for user type:', { isAdmin, isManager, isStaff, currentScreen });

            const registrationsSnapshot = await getDocs(registrationQuery);

            console.log('Found registrations:', registrationsSnapshot.docs.length);
            console.log('Registration documents:', registrationsSnapshot.docs.map((doc: any) => ({
                id: doc.id,
                data: doc.data()
            })));

            const existingRegistrations = registrationsSnapshot.docs.map((doc: any, index: any) => {
                const data = doc.data();
                return {
                    id: index + 1, // Local ID for UI
                    firestoreId: doc.id, // Firestore document ID
                    dateOfRegistration: data.dateOfRegistration || '',
                    registrationFee: data.registrationFee || 0,
                    discountAmount: data.discountAmount || 0,
                    afterDiscountAmount: data.afterDiscountAmount || 0,
                    paidAmount: data.paidAmount || 0,
                    receiptUrl: data.receiptUrl || '',
                    pendingAmount: data.pendingAmount || 0,
                    registrationFeeStatus: data.registrationFeeStatus || 'payment pending',
                    feeDetailId: data.feeDetailId || '',
                    feeDetailName: data.feeDetailName || '',
                    paymentType: data.paymentType || 'fullPayment',
                    numberOfInstallments: data.numberOfInstallments || 1,
                    installments: data.installments || [],
                    referredPerson: data.referredPerson || '',
                    referralAmount: data.referralAmount || 0,
                    userId: data.userId || '',
                    createdAt: data.createdAt || null,
                    updatedAt: data.updatedAt || null
                };
            }).sort((a: any, b: any) => {
                // Sort by createdAt (newest first), fallback to dateOfRegistration if createdAt is null
                const aDate = a.createdAt ? a.createdAt.toDate() : new Date(a.dateOfRegistration || '1970-01-01');
                const bDate = b.createdAt ? b.createdAt.toDate() : new Date(b.dateOfRegistration || '1970-01-01');
                return bDate.getTime() - aDate.getTime();
            }).map((registration: any, index: any) => ({
                ...registration,
                id: index + 1 // Reassign IDs after sorting
            }));

            setRegistrations(existingRegistrations);
            setNextId(existingRegistrations.length + 1);

            // Expand first registration if any exist
            if (existingRegistrations.length > 0) {
                setExpandedId(existingRegistrations[0].id);
            }

        } catch (err) {
            console.error('Error loading existing registrations:', err);
            setError('Failed to load existing registrations');
        }
    };

    useEffect(() => {
        if (userData?.companyId) {
            fetchRegistrationFeeDetails();
            loadExistingRegistrations();
        } else {
            console.error('No companyId available in userData');
        }
    }, [userData?.companyId]);

    const handleFileUpload = async (id: number, file: File) => {
        if (!userData?.companyId || !file) return;

        try {
            // Create a reference to the storage location
            const storageRef = ref(storage, `student_documents/${userData.companyId}/${leadId}/registration_receipts/${file.name}`);

            // Upload the file
            const snapshot = await uploadBytes(storageRef, file);

            // Get the download URL
            const downloadURL = await getDownloadURL(snapshot.ref);

            // Update the registration with the download URL
            updateRegistration(id, 'receiptUrl', downloadURL);

            return downloadURL;
        } catch (error) {
            console.error('Error uploading file:', error);
            setError('Failed to upload receipt');
            return null;
        }
    };

    // Add this function to handle full payment conversion
    const convertToFullPayment = async (registration: RegistrationData) => {
        if (!registration.installments) return;

        try {
            // Calculate total remaining amount across all pending installments
            const totalRemaining = registration.installments.reduce((sum, installment) => {
                const remaining = installment.installmentAmount - installment.paidAmount;
                return sum + Math.max(0, remaining);
            }, 0);

            // Update the registration to full payment mode
            const updatedRegistration: RegistrationData = {
                ...registration,
                paymentType: 'fullPayment' as const,
                numberOfInstallments: 1,
                installments: [],
                paidAmount: registration.paidAmount,
                pendingAmount: totalRemaining
            };

            // Update local state
            setRegistrations(prev => prev.map(reg =>
                reg.id === registration.id ? updatedRegistration : reg
            ));

            // Save to Firestore
            await saveRegistrationToFirestore(updatedRegistration);

            setShowFullPaymentModal(false);
            setSelectedRegistrationForFullPayment(null);

            // Show success message
            setError('Successfully converted to full payment!');
            setTimeout(() => setError(''), 3000);

            // then close the Manage Installments modal
            setShowInstallmentModal(false);
            setSelectedRegistrationForInstallments(null);

        } catch (err) {
            console.error('Error converting to full payment:', err);
            setError('Failed to convert to full payment');
        }
    };

    // Add this function to handle paying the full remaining amount
    // const payFullRemainingAmount = async (registration: RegistrationData) => {
    //     if (!registration.installments) return;

    //     try {
    //         setProcessingFullPayment(true);

    //         // Calculate total remaining amount across all pending installments
    //         const totalRemaining = registration.installments.reduce((sum, installment) => {
    //             const remaining = installment.installmentAmount - installment.paidAmount;
    //             return sum + Math.max(0, remaining);
    //         }, 0);

    //         // Update all pending installments to mark them as paid with their full installment amount
    //         const updatedInstallments = registration.installments.map(installment => {
    //             const remainingAmount = installment.installmentAmount - installment.paidAmount;
    //             if (remainingAmount > 0) {
    //                 return {
    //                     ...installment,
    //                     paidAmount: installment.installmentAmount, // Set paid amount to full installment amount
    //                     status: 'paid' as const
    //                 };
    //             }
    //             return installment;
    //         });

    //         // Calculate total paid amount from all installments
    //         const totalPaidFromInstallments = updatedInstallments.reduce((sum, inst) => sum + inst.paidAmount, 0);

    //         const updatedRegistration: RegistrationData = {
    //             ...registration,
    //             installments: updatedInstallments,
    //             paidAmount: totalPaidFromInstallments,
    //             pendingAmount: 0,
    //             registrationFeeStatus: 'fully paid'
    //         };

    //         // Store the updated registration for later use (before receipt upload)
    //         setRegistrationAfterFullPayment(updatedRegistration);

    //         // Show receipt upload modal immediately after payment processing
    //         setShowReceiptUploadModal(true);
    //         setShowInstallmentModal(false);

    //         // Show success message
    //         setError('Full payment processed successfully! Please upload receipt.');
    //         setTimeout(() => setError(''), 5000);

    //     } catch (err) {
    //         console.error('Error processing full payment:', err);
    //         setError('Failed to process full payment');
    //     } finally {
    //         setProcessingFullPayment(false);
    //     }
    // };


    // function to handle converting from full payment to installment
    const convertToInstallment = async (registration: RegistrationData) => {
        try {
            setProcessingFullPayment(true);

            // Use the existing pending amount (which already accounts for previous payments)
            const remainingAmount = registration.pendingAmount;

            // Create new installments based on the REMAINING amount
            const installmentAmount = Math.round(remainingAmount / newNumberOfInstallments);
            const remainderAmount = remainingAmount - (installmentAmount * (newNumberOfInstallments - 1));

            const newInstallments: InstallmentData[] = [];

            for (let i = 0; i < newNumberOfInstallments; i++) {
                const currentDate = new Date();
                currentDate.setMonth(currentDate.getMonth() + i);

                newInstallments.push({
                    installmentNumber: i + 1,
                    dueDate: currentDate.toISOString().split('T')[0],
                    installmentAmount: i === newNumberOfInstallments - 1 ? remainderAmount : installmentAmount,
                    paidAmount: 0, // Start with 0 paid for new installments
                    receiptUrl: '',
                    status: 'pending'
                });
            }

            // Keep the existing paidAmount from full payment - this is crucial!
            const updatedRegistration: RegistrationData = {
                ...registration,
                paymentType: 'installment' as const,
                numberOfInstallments: newNumberOfInstallments,
                installments: newInstallments,
                // paidAmount remains the same (preserves previous full payment)
                // pendingAmount remains the same (already calculated correctly)
                registrationFeeStatus: remainingAmount === 0 ? 'fully paid' :
                    registration.paidAmount > 0 ? 'partial payment done' : 'payment pending'
            };

            // Update local state
            setRegistrations(prev => prev.map(reg =>
                reg.id === registration.id ? updatedRegistration : reg
            ));

            // Save to Firestore
            await saveRegistrationToFirestore(updatedRegistration);

            // Close modal and reset state
            setShowConvertToInstallmentModal(false);
            setRegistrationForInstallmentConversion(null);
            setNewNumberOfInstallments(1);

            // Show success message
            setError('Successfully converted to installment plan!');
            setTimeout(() => setError(''), 3000);

        } catch (err) {
            console.error('Error converting to installment:', err);
            setError('Failed to convert to installment plan');
        } finally {
            setProcessingFullPayment(false);
        }
    };

    const openConvertToInstallmentModal = (registration: RegistrationData) => {
        setRegistrationForInstallmentConversion(registration);
        setNewNumberOfInstallments(1);
        setShowConvertToInstallmentModal(true);
    };


    // Save registration to Firestore
    const saveRegistrationToFirestore = async (registration: RegistrationData) => {
        if (!userData?.companyId) {
            setError('Company ID is missing');
            return;
        }

        try {
            setSavingStates(prev => ({ ...prev, [registration.id]: true }));

            const collectionPath = `companies/${userData.companyId}/registrationFormDetails`;
            const currentTimestamp = Timestamp.fromDate(new Date());

            // Prepare data for Firestore (include userId and leadId)
            const firestoreData = {
                dateOfRegistration: registration.dateOfRegistration,
                registrationFee: registration.registrationFee,
                discountAmount: registration.discountAmount,
                afterDiscountAmount: registration.afterDiscountAmount,
                paidAmount: registration.paidAmount,
                receiptUrl: registration.receiptUrl,
                pendingAmount: registration.pendingAmount,
                registrationFeeStatus: registration.registrationFeeStatus,
                feeDetailId: registration.feeDetailId,
                feeDetailName: registration.feeDetailName,
                paymentType: registration.paymentType || 'fullPayment',
                numberOfInstallments: registration.numberOfInstallments || 1,
                installments: registration.installments || [],
                referredPerson: registration.referredPerson || '',
                referralAmount: registration.referralAmount || 0,
                userId: auth.currentUser?.uid,           // Add userId from props
                leadId: leadId,           // Add leadId from props
                updatedAt: currentTimestamp
            };

            let firestoreId = registration.firestoreId;

            if (firestoreId) {
                // Update existing document
                await updateDoc(doc(db, collectionPath, firestoreId), firestoreData);
                console.log('Registration updated successfully:', firestoreId);
            } else {
                // Create new document
                const docRef = await addDoc(collection(db, collectionPath), {
                    ...firestoreData,
                    createdAt: serverTimestamp()
                });
                firestoreId = docRef.id;

                // Update local state with Firestore ID
                setRegistrations(prev => prev.map(reg =>
                    reg.id === registration.id
                        ? { ...reg, firestoreId: firestoreId }
                        : reg
                ));

                console.log('Registration saved successfully:', firestoreId);
            }

            // Update the lead's updatedAt field
            if (leadId) {
                const leadPath = `companies/${userData.companyId}/leads/${leadId}`;
                await updateDoc(doc(db, leadPath), {
                    updatedAt: currentTimestamp
                });
                console.log('Lead updatedAt field updated successfully');
            }

            // Show success message
            setError('Registration saved successfully!');
            setTimeout(() => setError(''), 3000);

        } catch (err) {
            console.error('Error saving registration:', err);
            const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred';
            // setError(`Failed to save registration: ${errorMessage}`);
        } finally {
            setSavingStates(prev => ({ ...prev, [registration.id]: false }));
        }
    };

    // Add function to handle receipt upload for full payment
    const handleFullPaymentReceiptUpload = async () => {
        if (!registrationAfterFullPayment || !fullPaymentReceiptFile) return;

        try {
            setProcessingFullPayment(true);

            // Upload the receipt file
            const downloadURL = await handleFileUpload(registrationAfterFullPayment.id, fullPaymentReceiptFile);

            if (downloadURL) {
                // Create the final registration data with receipt URL
                const finalRegistration: RegistrationData = {
                    ...registrationAfterFullPayment,
                    receiptUrl: downloadURL
                };

                // Update local state with the final data
                setRegistrations(prev => prev.map(reg =>
                    reg.id === registrationAfterFullPayment.id ? finalRegistration : reg
                ));

                // Save ALL data to Firestore (installments, paid amounts, status, and receipt)
                await saveRegistrationToFirestore(finalRegistration);

                // Close modal and reset state
                setShowReceiptUploadModal(false);
                setFullPaymentReceiptFile(null);
                setRegistrationAfterFullPayment(null);

                // Show success message
                setError('Full payment completed successfully with receipt uploaded! All data saved to database.');
                setTimeout(() => setError(''), 3000);
            }
        } catch (err) {
            console.error('Error uploading receipt and saving data:', err);
            setError('Failed to upload receipt and save data');
        } finally {
            setProcessingFullPayment(false);
        }
    };

    // Add function to skip receipt upload and save data anyway
    const handleSkipReceiptUpload = async () => {
        if (!registrationAfterFullPayment) return;

        try {
            setProcessingFullPayment(true);

            // Update local state with the registration data (without receipt)
            setRegistrations(prev => prev.map(reg =>
                reg.id === registrationAfterFullPayment.id ? registrationAfterFullPayment : reg
            ));

            // Save ALL data to Firestore (installments, paid amounts, status - without receipt)
            await saveRegistrationToFirestore(registrationAfterFullPayment);

            // Close modal and reset state
            setShowReceiptUploadModal(false);
            setFullPaymentReceiptFile(null);
            setRegistrationAfterFullPayment(null);

            // Show success message
            setError('Full payment completed successfully! Data saved to database.');
            setTimeout(() => setError(''), 3000);

        } catch (err) {
            console.error('Error saving data without receipt:', err);
            setError('Failed to save payment data');
        } finally {
            setProcessingFullPayment(false);
        }
    };

    // Delete registration from Firestore
    const deleteRegistrationFromFirestore = async (registration: RegistrationData) => {
        if (!userData?.companyId || !registration.firestoreId) return;

        try {
            const collectionPath = `companies/${userData.companyId}/registrationFormDetails`;
            await deleteDoc(doc(db, collectionPath, registration.firestoreId));
            console.log('Registration deleted from Firestore:', registration.firestoreId);
        } catch (err) {
            console.error('Error deleting registration from Firestore:', err);
            setError('Failed to delete registration from database');
        }
    };

    const addNewRegistration = () => {
        if (selectedFeeDetail && paymentType) {
            // Calculate discount amount - for full payment, use 0; for installment, use modal value
            const finalDiscountAmount = paymentType === 'installment' ? modalDiscountAmount : 0;
            // Add referral amount to the original fee amount
            const afterDiscountAmount = selectedFeeDetail.registrationFeeAmount - finalDiscountAmount + modalReferralAmount;

            // Create installments if payment type is installment
            let installments: InstallmentData[] = [];
            if (paymentType === 'installment') {
                const installmentAmount = Math.round(afterDiscountAmount / numberOfInstallments);
                const remainderAmount = afterDiscountAmount - (installmentAmount * (numberOfInstallments - 1));

                for (let i = 0; i < numberOfInstallments; i++) {
                    const currentDate = new Date();
                    currentDate.setMonth(currentDate.getMonth() + i); // Each installment due next month

                    installments.push({
                        installmentNumber: i + 1,
                        dueDate: currentDate.toISOString().split('T')[0],
                        installmentAmount: i === numberOfInstallments - 1 ? remainderAmount : installmentAmount,
                        paidAmount: 0,
                        receiptUrl: '',
                        status: 'pending'
                    });
                }
            }

            const newRegistration: RegistrationData = {
                id: nextId,
                dateOfRegistration: new Date().toISOString().split('T')[0],
                registrationFee: selectedFeeDetail.registrationFeeAmount,
                discountAmount: finalDiscountAmount,
                afterDiscountAmount: afterDiscountAmount,
                paidAmount: 0,
                receiptUrl: '',
                pendingAmount: afterDiscountAmount,
                registrationFeeStatus: 'payment pending',
                feeDetailId: selectedFeeDetail.id,
                feeDetailName: selectedFeeDetail.registrationFeeName,
                paymentType: paymentType,
                numberOfInstallments: paymentType === 'installment' ? numberOfInstallments : 1,
                installments: installments,
                referredPerson: modalReferredPerson || '',
                referralAmount: modalReferralAmount || 0
            };

            // Add new registration at the beginning to maintain "latest first" order
            setRegistrations([newRegistration, ...registrations]);
            setExpandedId(nextId);
            setNextId(nextId + 1);

            // Reset modal state
            setSelectedFeeDetail(null);
            setPaymentType('');
            setNumberOfInstallments(1);
            setModalDiscountAmount(0);
            setModalReferredPerson('');
            setModalReferralAmount(0);
            setShowAddModal(false);
        }
    };

    const deleteRegistration = async (id: number) => {
        const registrationToDelete = registrations.find(reg => reg.id === id);

        if (registrationToDelete) {
            // Delete from Firestore if it exists there
            await deleteRegistrationFromFirestore(registrationToDelete);
        }

        const updatedRegistrations = registrations.filter(reg => reg.id !== id);
        setRegistrations(updatedRegistrations);

        setDropdownStates(prev => {
            const newState = { ...prev };
            delete newState[id];
            return newState;
        });

        if (expandedId === id) {
            const firstId = updatedRegistrations.length > 0 ? updatedRegistrations[0].id : null;
            setExpandedId(firstId);
        }

        // Close confirmation dialog
        setDeleteConfirmation({ show: false, registrationId: null });
    };

    const handleDeleteClick = (id: number) => {
        setDeleteConfirmation({ show: true, registrationId: id });
    };

    const cancelDelete = () => {
        setDeleteConfirmation({ show: false, registrationId: null });
    };

    const toggleExpanded = (id: number) => {
        setExpandedId(expandedId === id ? null : id);
    };

    const toggleStatusDropdown = (id: number) => {
        setDropdownStates(prev => ({
            ...prev,
            [id]: !prev[id]
        }));
    };

    const handleStatusSelect = (id: number, option: string) => {
        setRegistrations(prev => prev.map(reg =>
            reg.id === id ? { ...reg, registrationFeeStatus: option } : reg
        ));
        setDropdownStates(prev => ({
            ...prev,
            [id]: false
        }));
    };

    const updateRegistration = (id: number, field: string, value: any) => {
        setRegistrations(prev => prev.map(reg => {
            if (reg.id === id) {
                const updated = { ...reg, [field]: value };

                // When referral amount changes, recalculate afterDiscountAmount and pendingAmount
                // but don't change registrationFee (as requested)
                if (field === 'referralAmount') {
                    // afterDiscountAmount = registrationFee - discountAmount + referralAmount
                    updated.afterDiscountAmount = (updated.registrationFee || 0) - (updated.discountAmount || 0) + (value || 0);
                    updated.pendingAmount = updated.afterDiscountAmount - (updated.paidAmount || 0);
                }

                if (field === 'registrationFee' || field === 'discountAmount') {
                    // afterDiscountAmount = registrationFee - discountAmount + referralAmount
                    updated.afterDiscountAmount = (updated.registrationFee || 0) - (updated.discountAmount || 0) + (updated.referralAmount || 0);
                }

                if (field === 'registrationFee' || field === 'discountAmount' || field === 'paidAmount') {
                    const afterDiscount = field === 'registrationFee' || field === 'discountAmount'
                        ? (updated.registrationFee || 0) - (updated.discountAmount || 0) + (updated.referralAmount || 0)
                        : updated.afterDiscountAmount;
                    updated.pendingAmount = afterDiscount - (updated.paidAmount || 0);
                }

                return updated;
            }
            return reg;
        }));
    };

    // Installment management functions
    const openInstallmentModal = (registration: RegistrationData) => {
        setSelectedRegistrationForInstallments(registration);
        setCurrentInstallmentIndex(0);
        setShowInstallmentModal(true);
    };

    // Function to redistribute overpayment across remaining installments
    // Example: If user pays ₹1200 for installment 1 (original amount ₹1000), 
    // the ₹200 overpayment is redistributed across remaining installments by recalculating their amounts
    const redistributeOverpayment = (installments: InstallmentData[], totalAfterDiscount: number) => {
        const updatedInstallments = [...installments];

        // Calculate total amount already paid
        const totalPaid = updatedInstallments.reduce((sum, inst) => sum + inst.paidAmount, 0);

        // Calculate remaining balance to be distributed among pending installments
        const remainingBalance = Math.max(0, totalAfterDiscount - totalPaid);

        // Find pending installments (those with paid amount less than installment amount)
        const pendingInstallments = updatedInstallments.filter((inst, index) => {
            return inst.paidAmount < inst.installmentAmount;
        });

        if (pendingInstallments.length > 0 && remainingBalance > 0) {
            // Calculate new installment amount for remaining installments
            const newInstallmentAmount = Math.round(remainingBalance / pendingInstallments.length);
            const remainder = remainingBalance - (newInstallmentAmount * (pendingInstallments.length - 1));

            // Update installment amounts for pending installments only
            let pendingIndex = 0;
            for (let i = 0; i < updatedInstallments.length; i++) {
                if (updatedInstallments[i].paidAmount < updatedInstallments[i].installmentAmount) {
                    if (pendingIndex === pendingInstallments.length - 1) {
                        // Last pending installment gets the remainder to handle rounding
                        updatedInstallments[i].installmentAmount = remainder;
                    } else {
                        updatedInstallments[i].installmentAmount = newInstallmentAmount;
                    }
                    pendingIndex++;
                }
            }
        }

        return updatedInstallments;
    };

    const updateInstallment = (registrationId: number, installmentIndex: number, field: keyof InstallmentData, value: any) => {
        setRegistrations(prev => prev.map(reg => {
            if (reg.id === registrationId && reg.installments) {
                const updatedInstallments = [...reg.installments];

                // Store the old paid amount for this installment before updating
                const oldPaidAmount = updatedInstallments[installmentIndex].paidAmount;

                // Update the field
                updatedInstallments[installmentIndex] = {
                    ...updatedInstallments[installmentIndex],
                    [field]: value
                };

                // Handle paid amount changes
                if (field === 'paidAmount') {
                    const currentInstallment = updatedInstallments[installmentIndex];
                    const netChange = value - oldPaidAmount;

                    // Calculate total paid amount including this change
                    const newTotalPaidAmount = reg.paidAmount + netChange;

                    // Calculate remaining balance after all payments
                    const remainingBalance = Math.max(0, reg.afterDiscountAmount - newTotalPaidAmount);

                    // Count remaining pending installments (after current one)
                    const pendingInstallments = updatedInstallments.filter((inst, idx) =>
                        idx > installmentIndex && inst.paidAmount < inst.installmentAmount
                    );

                    // If there are pending installments and remaining balance, redistribute
                    if (pendingInstallments.length > 0 && remainingBalance > 0) {
                        // Calculate new installment amounts for pending installments
                        const newInstallmentAmount = Math.floor(remainingBalance / pendingInstallments.length);
                        const remainder = remainingBalance - (newInstallmentAmount * (pendingInstallments.length - 1));

                        // Update pending installment amounts
                        let pendingIndex = 0;
                        for (let i = installmentIndex + 1; i < updatedInstallments.length; i++) {
                            if (updatedInstallments[i].paidAmount < updatedInstallments[i].installmentAmount) {
                                if (pendingIndex === pendingInstallments.length - 1) {
                                    updatedInstallments[i].installmentAmount = remainder;
                                } else {
                                    updatedInstallments[i].installmentAmount = newInstallmentAmount;
                                }
                                pendingIndex++;
                            }
                        }
                    }

                    // Update installment status
                    if (value === 0) {
                        currentInstallment.status = 'pending';
                    } else if (value >= currentInstallment.installmentAmount) {
                        currentInstallment.status = 'paid';
                    } else {
                        currentInstallment.status = 'partial';
                    }

                    // Final calculations after redistribution
                    const finalTotalPaidAmount = reg.paidAmount + netChange;
                    const finalPendingAmount = reg.afterDiscountAmount - finalTotalPaidAmount;

                    const updatedReg = {
                        ...reg,
                        installments: updatedInstallments,
                        paidAmount: finalTotalPaidAmount,
                        pendingAmount: finalPendingAmount
                    };

                    // Update registration status based on TOTAL payment
                    if (finalTotalPaidAmount === 0) {
                        updatedReg.registrationFeeStatus = 'payment pending';
                    } else if (finalTotalPaidAmount >= reg.afterDiscountAmount) {
                        updatedReg.registrationFeeStatus = 'fully paid';
                    } else {
                        updatedReg.registrationFeeStatus = 'partial payment done';
                    }

                    return updatedReg;
                }

                return { ...reg, installments: updatedInstallments };
            }
            return reg;
        }));

        // Also update the modal state
        setSelectedRegistrationForInstallments(prev => {
            if (prev && prev.id === registrationId) {
                const updatedRegistrations = registrations.map(reg => {
                    if (reg.id === registrationId && reg.installments) {
                        const updatedInstallments = [...reg.installments];
                        const oldPaidAmount = updatedInstallments[installmentIndex].paidAmount;

                        updatedInstallments[installmentIndex] = {
                            ...updatedInstallments[installmentIndex],
                            [field]: value
                        };

                        if (field === 'paidAmount') {
                            const currentInstallment = updatedInstallments[installmentIndex];
                            const netChange = value - oldPaidAmount;
                            const newTotalPaidAmount = reg.paidAmount + netChange;
                            const remainingBalance = Math.max(0, reg.afterDiscountAmount - newTotalPaidAmount);

                            const pendingInstallments = updatedInstallments.filter((inst, idx) =>
                                idx > installmentIndex && inst.paidAmount < inst.installmentAmount
                            );

                            if (pendingInstallments.length > 0 && remainingBalance > 0) {
                                const newInstallmentAmount = Math.floor(remainingBalance / pendingInstallments.length);
                                const remainder = remainingBalance - (newInstallmentAmount * (pendingInstallments.length - 1));

                                let pendingIndex = 0;
                                for (let i = installmentIndex + 1; i < updatedInstallments.length; i++) {
                                    if (updatedInstallments[i].paidAmount < updatedInstallments[i].installmentAmount) {
                                        if (pendingIndex === pendingInstallments.length - 1) {
                                            updatedInstallments[i].installmentAmount = remainder;
                                        } else {
                                            updatedInstallments[i].installmentAmount = newInstallmentAmount;
                                        }
                                        pendingIndex++;
                                    }
                                }
                            }

                            // Update status
                            if (value === 0) {
                                currentInstallment.status = 'pending';
                            } else if (value >= currentInstallment.installmentAmount) {
                                currentInstallment.status = 'paid';
                            } else {
                                currentInstallment.status = 'partial';
                            }

                            const finalTotalPaidAmount = reg.paidAmount + netChange;
                            const finalPendingAmount = reg.afterDiscountAmount - finalTotalPaidAmount;

                            const updatedReg = {
                                ...reg,
                                installments: updatedInstallments,
                                paidAmount: finalTotalPaidAmount,
                                pendingAmount: finalPendingAmount
                            };

                            if (finalTotalPaidAmount === 0) {
                                updatedReg.registrationFeeStatus = 'payment pending';
                            } else if (finalTotalPaidAmount >= reg.afterDiscountAmount) {
                                updatedReg.registrationFeeStatus = 'fully paid';
                            } else {
                                updatedReg.registrationFeeStatus = 'partial payment done';
                            }

                            return updatedReg;
                        }

                        return { ...reg, installments: updatedInstallments };
                    }
                    return reg;
                });
                const updatedReg = updatedRegistrations.find(reg => reg.id === registrationId);
                return updatedReg || prev;
            }
            return prev;
        });
    };

    const redistributeInstallmentAmounts = (registrationId: number, newInstallmentAmount: number, installmentIndex: number) => {
        setRegistrations(prev => prev.map(reg => {
            if (reg.id === registrationId && reg.installments) {
                const updatedInstallments = [...reg.installments];
                const totalAfterDiscount = reg.afterDiscountAmount;

                // Update current installment amount
                updatedInstallments[installmentIndex].installmentAmount = newInstallmentAmount;

                // Calculate remaining amount to distribute
                const usedAmount = newInstallmentAmount;
                const remainingAmount = totalAfterDiscount - usedAmount;
                const remainingInstallments = updatedInstallments.length - 1;

                if (remainingInstallments > 0) {
                    const equalAmount = Math.round(remainingAmount / remainingInstallments);
                    const remainder = remainingAmount - (equalAmount * (remainingInstallments - 1));

                    let remainingIndex = 0;
                    for (let i = 0; i < updatedInstallments.length; i++) {
                        if (i !== installmentIndex) {
                            if (remainingIndex === remainingInstallments - 1) {
                                updatedInstallments[i].installmentAmount = remainder;
                            } else {
                                updatedInstallments[i].installmentAmount = equalAmount;
                            }
                            remainingIndex++;
                        }
                    }
                }

                return { ...reg, installments: updatedInstallments };
            }
            return reg;
        }));

        // Update the selected registration for the modal
        setSelectedRegistrationForInstallments(prev => {
            if (prev && prev.id === registrationId) {
                const updatedRegistrations = registrations.map(reg => {
                    if (reg.id === registrationId && reg.installments) {
                        const updatedInstallments = [...reg.installments];
                        const totalAfterDiscount = reg.afterDiscountAmount;

                        // Update current installment amount
                        updatedInstallments[installmentIndex].installmentAmount = newInstallmentAmount;

                        // Calculate remaining amount to distribute
                        const usedAmount = newInstallmentAmount;
                        const remainingAmount = totalAfterDiscount - usedAmount;
                        const remainingInstallments = updatedInstallments.length - 1;

                        if (remainingInstallments > 0) {
                            const equalAmount = Math.round(remainingAmount / remainingInstallments);
                            const remainder = remainingAmount - (equalAmount * (remainingInstallments - 1));

                            let remainingIndex = 0;
                            for (let i = 0; i < updatedInstallments.length; i++) {
                                if (i !== installmentIndex) {
                                    if (remainingIndex === remainingInstallments - 1) {
                                        updatedInstallments[i].installmentAmount = remainder;
                                    } else {
                                        updatedInstallments[i].installmentAmount = equalAmount;
                                    }
                                    remainingIndex++;
                                }
                            }
                        }

                        return { ...reg, installments: updatedInstallments };
                    }
                    return reg;
                });
                const updatedReg = updatedRegistrations.find(reg => reg.id === registrationId);
                return updatedReg || prev;
            }
            return prev;
        });
    };

    const getCollapsedSummary = (registration: RegistrationData) => {
        return `${registration.dateOfRegistration || 'No date'} • ₹${registration.registrationFee || 0} • ${registration.registrationFeeStatus}`;
    };



    const renderRegistrationForm = (registration: RegistrationData) => {
        const isExpanded = expandedId === registration.id;
        const isDropdownOpen = dropdownStates[registration.id] || false;
        const isSaving = savingStates[registration.id] || false;

        return (
            <div key={registration.id} className={sectionClass}>
                <div className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 rounded-t-lg">
                    <div
                        className="flex items-center space-x-3 flex-1"
                        onClick={() => toggleExpanded(registration.id)}
                    >
                        <h3 className="text-lg font-semibold text-sky-950">
                            {registration.feeDetailName || `Registration Detail #${registration.id}`}
                        </h3>
                        {!isExpanded && (
                            <span className="text-sm text-gray-600">
                                {getCollapsedSummary(registration)}
                            </span>
                        )}
                        {registration.firestoreId && (
                            <span className="text-xs text-green-600 bg-green-100 px-2 py-1 rounded-full">
                                Saved
                            </span>
                        )}
                    </div>
                    {/* Convert to Installment option for full payment registrations with pending amount */}
                    {registration.paymentType === 'fullPayment' && registration.pendingAmount > 0 && write && (
                        <div className="md:col-span-2">
                            <button
                                onClick={() => openConvertToInstallmentModal(registration)}
                                className="px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 text-sm"
                            >
                                Convert to Installment Plan
                            </button>
                        </div>
                    )}
                    <div className="flex items-center space-x-2">
                        {write && registrations.length > 0 && (
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteClick(registration.id);
                                }}
                                className="p-2 text-red-500 hover:bg-red-50 rounded-full transition-colors"
                                title="Delete Registration"
                            >
                                <Trash2 size={18} />
                            </button>
                        )}
                        <button
                            onClick={() => toggleExpanded(registration.id)}
                            className="p-1"
                        >
                            {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                        </button>
                    </div>
                </div>

                {isExpanded && (
                    <div className="p-6 pt-0">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label htmlFor={`dateOfRegistration-${registration.id}`} className={labelClass}>
                                    Date of Registration
                                </label>
                                <input
                                    type="date"
                                    id={`dateOfRegistration-${registration.id}`}
                                    value={registration.dateOfRegistration}
                                    onChange={(e) => updateRegistration(registration.id, 'dateOfRegistration', e.target.value)}
                                    className={inputClass}
                                    disabled={!write}
                                />
                            </div>

                            <div>
                                <label htmlFor={`registrationFee-${registration.id}`} className={labelClass}>
                                    Registration Fee
                                </label>
                                <div className="relative">
                                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2">₹</span>
                                    <input
                                        type="number"
                                        id={`registrationFee-${registration.id}`}
                                        value={registration.registrationFee === 0 ? '' : registration.registrationFee}
                                        onChange={(e) => updateRegistration(registration.id, 'registrationFee', Number(e.target.value) || 0)}
                                        className={`${inputClass} pl-8`}
                                        disabled={!write}
                                        readOnly
                                    />
                                </div>
                            </div>

                            <div>
                                <label htmlFor={`discountAmount-${registration.id}`} className={labelClass}>
                                    Discount Amount
                                </label>
                                <div className="relative">
                                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2">₹</span>
                                    <input
                                        type="number"
                                        id={`discountAmount-${registration.id}`}
                                        value={registration.discountAmount === 0 ? '' : registration.discountAmount}
                                        onChange={(e) => updateRegistration(registration.id, 'discountAmount', Number(e.target.value) || 0)}
                                        className={`${inputClass} pl-8`}
                                        disabled={!write}
                                    />
                                </div>
                            </div>

                            <div>
                                <label htmlFor={`referredPerson-${registration.id}`} className={labelClass}>
                                    Referred Person
                                </label>
                                <input
                                    type="text"
                                    id={`referredPerson-${registration.id}`}
                                    value={registration.referredPerson || ''}
                                    onChange={(e) => updateRegistration(registration.id, 'referredPerson', e.target.value)}
                                    className={inputClass}
                                    disabled={!write}
                                    placeholder="Enter referred person name"
                                />
                            </div>

                            <div>
                                <label htmlFor={`referralAmount-${registration.id}`} className={labelClass}>
                                    Referral Amount
                                </label>
                                <div className="relative">
                                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2">₹</span>
                                    <input
                                        type="number"
                                        id={`referralAmount-${registration.id}`}
                                        value={registration.referralAmount === 0 ? '' : registration.referralAmount}
                                        onChange={(e) => updateRegistration(registration.id, 'referralAmount', Number(e.target.value) || 0)}
                                        className={`${inputClass} pl-8`}
                                        disabled={!write}
                                    />
                                </div>
                            </div>

                            <div>
                                <label htmlFor={`afterDiscountAmount-${registration.id}`} className={labelClass}>
                                    Total Amount
                                </label>
                                <div className="relative">
                                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2">₹</span>
                                    <input
                                        type="number"
                                        id={`afterDiscountAmount-${registration.id}`}
                                        value={registration.afterDiscountAmount === 0 ? '' : registration.afterDiscountAmount}
                                        className={`${inputClass} pl-8 bg-gray-100`}
                                        readOnly
                                    />
                                </div>
                            </div>

                            {/* Conditional rendering based on payment type */}
                            {registration.paymentType === 'installment' && registration.installments ? (
                                // Installment Box
                                <div className="md:col-span-2">
                                    <label className={labelClass}>Installment Details</label>
                                    <div
                                        onClick={() => openInstallmentModal(registration)}
                                        className="w-full p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 cursor-pointer transition-colors bg-gray-50 hover:bg-blue-50"
                                    >
                                        <div className="text-center">
                                            <div className="text-lg font-semibold text-gray-700">
                                                {registration.numberOfInstallments} Installments
                                            </div>
                                            <div className="text-sm text-gray-600 mt-1">
                                                Paid: ₹{registration.paidAmount} | Pending: ₹{registration.pendingAmount}
                                            </div>
                                            <div className="text-xs text-blue-600 mt-2">
                                                Click to manage installments
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                // Regular payment fields for full payment
                                <>
                                    <div>
                                        <label htmlFor={`paidAmount-${registration.id}`} className={labelClass}>
                                            Paid Amount
                                        </label>
                                        <div className="relative">
                                            <span className="absolute left-3 top-1/2 transform -translate-y-1/2">₹</span>
                                            <input
                                                type="number"
                                                id={`paidAmount-${registration.id}`}
                                                value={registration.paidAmount === 0 ? '' : registration.paidAmount}
                                                onChange={(e) => updateRegistration(registration.id, 'paidAmount', Number(e.target.value) || 0)}
                                                className={`${inputClass} pl-8`}
                                                disabled={!write}
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label htmlFor={`pendingAmount-${registration.id}`} className={labelClass}>
                                            Pending Amount
                                        </label>
                                        <div className="relative">
                                            <span className="absolute left-3 top-1/2 transform -translate-y-1/2">₹</span>
                                            <input
                                                type="number"
                                                id={`pendingAmount-${registration.id}`}
                                                value={registration.pendingAmount === 0 ? '' : registration.pendingAmount}
                                                className={`${inputClass} pl-8 bg-gray-100`}
                                                readOnly
                                            />
                                        </div>
                                    </div>
                                </>
                            )}

                            <div className="relative">
                                <label htmlFor={`registrationFeeStatus-${registration.id}`} className={labelClass}>
                                    Registration Fee Status
                                </label>
                                <div className="p-[2px] bg-gradient-to-b from-[#02385E] to-[#02385E] rounded-3xl">
                                    <div className="relative">
                                        <input
                                            type="text"
                                            id={`registrationFeeStatus-${registration.id}`}
                                            value={registration.registrationFeeStatus}
                                            onClick={() => write && toggleStatusDropdown(registration.id)}
                                            className={`w-full py-2 pl-10 pr-16 bg-white text-gray-800 rounded-3xl focus:outline-none ${!write ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                                            readOnly
                                            disabled={!write}
                                        />
                                        {write && (
                                            <button
                                                type="button"
                                                className="absolute right-10 top-1/2 transform -translate-y-1/2"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    updateRegistration(registration.id, 'registrationFeeStatus', '');
                                                }}
                                            >
                                                <span className="text-gray-500">×</span>
                                            </button>
                                        )}
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    className="absolute right-3 top-12 transform -translate-y-1/2"
                                    onClick={() => write && toggleStatusDropdown(registration.id)}
                                    disabled={!write}
                                >
                                    <span className={`text-gray-500 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`}>▼</span>
                                </button>
                                {isDropdownOpen && (
                                    <div className="absolute left-0 right-0 mt-1 bg-white border rounded-3xl shadow-lg z-10">
                                        {statusOptions.map((option) => (
                                            <button
                                                key={option}
                                                onClick={() => handleStatusSelect(registration.id, option)}
                                                className="w-full py-2 px-4 text-left text-gray-800 hover:bg-gray-100 first:rounded-t-3xl last:rounded-b-3xl"
                                            >
                                                {option}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* the receipt upload section will seen only for the fullPayment */}
                            {registration.paymentType === 'fullPayment' && (
                                <div>
                                    <label htmlFor={`receipt-${registration.id}`} className={labelClass}>
                                        Upload Receipt
                                    </label>
                                    <div className="flex items-center">
                                        <input
                                            type="file"
                                            id={`receipt-${registration.id}`}
                                            className="hidden"
                                            disabled={!write}
                                            onChange={async (e) => {
                                                const file = e.target.files?.[0];
                                                if (file) {
                                                    await handleFileUpload(registration.id, file);
                                                }
                                            }}
                                        />
                                        <label
                                            htmlFor={`receipt-${registration.id}`}
                                            className={`px-4 py-2 bg-gray-100 rounded-l-3xl border border-gray-300 ${!write ? 'cursor-not-allowed' : 'cursor-pointer hover:bg-gray-200'}`}
                                        >
                                            Choose File
                                        </label>
                                        <input
                                            type="text"
                                            value={registration.receiptUrl ? registration.receiptUrl.split('/').pop() || 'File selected' : 'No file chosen'}
                                            className={`flex-1 py-2 px-4 border border-gray-300 border-l-0 rounded-r-3xl bg-white ${!write ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                                            readOnly
                                        />
                                    </div>
                                    {registration.receiptUrl && (
                                        <a
                                            href={registration.receiptUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-blue-600 text-sm mt-1 inline-block"
                                        >
                                            View current receipt
                                        </a>
                                    )}
                                </div>
                            )}
                        </div>

                        {write && (
                            <div className="flex justify-center mt-8">
                                <button
                                    className={`px-8 py-3 text-white rounded-3xl font-medium transition-all ${isSaving
                                        ? 'bg-gray-400 cursor-not-allowed'
                                        : 'bg-gradient-to-r from-[#3483BE] to-[#02385E] hover:shadow-lg'
                                        }`}
                                    onClick={() => saveRegistrationToFirestore(registration)}
                                    disabled={isSaving}
                                >
                                    {isSaving ? (
                                        <div className="flex items-center space-x-2">
                                            <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                                            <span>Saving...</span>
                                        </div>
                                    ) : (
                                        'Save'
                                    )}
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="p-8">
            <div className="flex items-center justify-between mb-6">
                <h1 className="text-2xl font-bold text-sky-950">Registration Details</h1>
                {write && (
                    <button
                        onClick={() => setShowAddModal(true)}
                        className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-[#3483BE] to-[#02385E] text-white rounded-full hover:shadow-lg transition-shadow"
                        disabled={loadingFeeDetails}
                    >
                        <Plus size={20} />
                        <span>Add Registration</span>
                    </button>
                )}
            </div>

            {error && (
                <div className={`mb-4 p-4 rounded-lg ${error.includes('successfully')
                    ? 'bg-green-100 text-green-700'
                    : 'bg-red-100 text-red-700'
                    }`}>
                    {error}
                </div>
            )}

            {registrations.length > 0 ? (
                registrations.map(registration => renderRegistrationForm(registration))
            ) : (
                <div className="text-center py-12">
                    <p className="text-gray-500 mb-4">No registration details added yet.</p>
                    {write && (
                        <button
                            onClick={() => setShowAddModal(true)}
                            className="px-6 py-3 bg-gradient-to-r from-[#3483BE] to-[#02385E] text-white rounded-full hover:shadow-lg transition-shadow"
                            disabled={loadingFeeDetails}
                        >
                            Add First Registration
                        </button>
                    )}
                </div>
            )}

            {showAddModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-md">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-semibold">Add New Registration</h2>
                            <button
                                onClick={() => {
                                    setShowAddModal(false);
                                    setSelectedFeeDetail(null);
                                    setPaymentType('');
                                    setNumberOfInstallments(1);
                                    setModalDiscountAmount(0);
                                }}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                <X size={24} />
                            </button>
                        </div>

                        <div className="mb-4">
                            <label className={labelClass}>Select Registration Type</label>
                            {loadingFeeDetails ? (
                                <div className="flex justify-center py-4">
                                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
                                    <span className="ml-2">Loading options...</span>
                                </div>
                            ) : error && !error.includes('successfully') ? (
                                <div className="text-red-500 p-2 bg-red-50 rounded-lg">
                                    {error}
                                    <button
                                        onClick={fetchRegistrationFeeDetails}
                                        className="ml-2 text-blue-500 hover:text-blue-700"
                                    >
                                        Retry
                                    </button>
                                </div>
                            ) : availableFeeDetails.length === 0 ? (
                                <div className="text-yellow-600 p-2 bg-yellow-50 rounded-lg">
                                    No registration fee types found in database
                                </div>
                            ) : (
                                <div className="relative">
                                    <select
                                        value={selectedFeeDetail?.id || ''}
                                        onChange={(e) => {
                                            const selected = availableFeeDetails.find(d => d.id === e.target.value);
                                            console.log('Selected option:', selected);
                                            setSelectedFeeDetail(selected || null);
                                            // Reset payment type when registration type changes
                                            setPaymentType('');
                                            setModalDiscountAmount(0);
                                        }}
                                        className={`${inputClass} appearance-none`}
                                    >
                                        <option value="">Select a registration type</option>
                                        {availableFeeDetails.map((detail) => {
                                            const isAlreadyAdded = registrations.some(r => r.feeDetailId === detail.id);
                                            return (
                                                <option
                                                    key={detail.id}
                                                    value={detail.id}
                                                    disabled={isAlreadyAdded}
                                                    className={isAlreadyAdded ? 'text-gray-400' : ''}
                                                >
                                                    {detail.registrationFeeName} (₹{detail.registrationFeeAmount})
                                                    {isAlreadyAdded && ' (already added)'}
                                                </option>
                                            );
                                        })}
                                    </select>
                                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                                        <ChevronDown size={20} />
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Payment Type Selection */}
                        {selectedFeeDetail && (
                            <div className="mb-4">
                                <label className={labelClass}>Payment Type</label>
                                <div className="space-y-2">
                                    <label className="flex items-center">
                                        <input
                                            type="radio"
                                            name="paymentType"
                                            value="fullPayment"
                                            checked={paymentType === 'fullPayment'}
                                            onChange={(e) => {
                                                setPaymentType(e.target.value as 'fullPayment');
                                                setModalDiscountAmount(0);
                                            }}
                                            className="mr-2"
                                        />
                                        Full Payment
                                    </label>
                                    <label className="flex items-center">
                                        <input
                                            type="radio"
                                            name="paymentType"
                                            value="installment"
                                            checked={paymentType === 'installment'}
                                            onChange={(e) => {
                                                setPaymentType(e.target.value as 'installment');
                                            }}
                                            className="mr-2"
                                        />
                                        Installment
                                    </label>
                                </div>
                            </div>
                        )}

                        {/* Installment Options */}
                        {selectedFeeDetail && paymentType === 'installment' && (
                            <div className="mb-4 space-y-4">
                                <div>
                                    <label className={labelClass}>Number of Installments</label>
                                    <div className="relative">
                                        <select
                                            value={numberOfInstallments}
                                            onChange={(e) => setNumberOfInstallments(Number(e.target.value))}
                                            className={`${inputClass} appearance-none`}
                                        >
                                            {Array.from({ length: 12 }, (_, i) => i + 1).map((num) => (
                                                <option key={num} value={num}>
                                                    {num} {num === 1 ? 'installment' : 'installments'}
                                                </option>
                                            ))}
                                        </select>
                                        <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                                            <ChevronDown size={20} />
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    <label className={labelClass}>Discount Amount (₹)</label>
                                    <input
                                        type="number"
                                        min="0"
                                        max={selectedFeeDetail.registrationFeeAmount}
                                        value={modalDiscountAmount === 0 ? '' : modalDiscountAmount}
                                        onChange={(e) => {
                                            const value = Math.max(0, Math.min(Number(e.target.value) || 0, selectedFeeDetail.registrationFeeAmount));
                                            setModalDiscountAmount(value);
                                        }}
                                        className={inputClass}
                                        placeholder="Enter discount amount"
                                    />
                                </div>
                            </div>
                        )}

                        {/* Referral Fields - Show when payment type is selected */}
                        {selectedFeeDetail && paymentType && (
                            <div className="mb-4 space-y-4">
                                <div>
                                    <label className={labelClass}>Referred Person</label>
                                    <input
                                        type="text"
                                        value={modalReferredPerson}
                                        onChange={(e) => setModalReferredPerson(e.target.value)}
                                        className={inputClass}
                                        placeholder="Enter referred person name (optional)"
                                    />
                                </div>

                                <div>
                                    <label className={labelClass}>Referral Amount (₹)</label>
                                    <input
                                        type="number"
                                        min="0"
                                        value={modalReferralAmount === 0 ? '' : modalReferralAmount}
                                        onChange={(e) => {
                                            const value = Math.max(0, Number(e.target.value) || 0);
                                            setModalReferralAmount(value);
                                        }}
                                        className={inputClass}
                                        placeholder="Enter referral amount (optional)"
                                    />
                                </div>
                            </div>
                        )}

                        {selectedFeeDetail && (
                            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                                <h3 className="font-medium mb-2">Registration Summary:</h3>
                                <p><strong>Name:</strong> {selectedFeeDetail.registrationFeeName}</p>
                                <p><strong>Original Amount:</strong> ₹{selectedFeeDetail.registrationFeeAmount}</p>
                                {paymentType === 'installment' && modalDiscountAmount > 0 && (
                                    <>
                                        <p><strong>Discount:</strong> ₹{modalDiscountAmount}</p>
                                        <p><strong>After Discount:</strong> ₹{selectedFeeDetail.registrationFeeAmount - modalDiscountAmount}</p>
                                    </>
                                )}
                                {modalReferralAmount > 0 && (
                                    <>
                                        <p><strong>Referral Amount:</strong> ₹{modalReferralAmount}</p>
                                        <p><strong>Final Amount:</strong> ₹{selectedFeeDetail.registrationFeeAmount - (paymentType === 'installment' ? modalDiscountAmount : 0) + modalReferralAmount}</p>
                                    </>
                                )}
                                {paymentType === 'installment' && (
                                    <p><strong>Installments:</strong> {numberOfInstallments}</p>
                                )}
                                {paymentType && (
                                    <p><strong>Payment Type:</strong> {paymentType === 'fullPayment' ? 'Full Payment' : 'Installment'}</p>
                                )}
                                {modalReferredPerson && (
                                    <p><strong>Referred Person:</strong> {modalReferredPerson}</p>
                                )}
                            </div>
                        )}

                        <div className="flex justify-end space-x-3">
                            <button
                                onClick={() => {
                                    setShowAddModal(false);
                                    setSelectedFeeDetail(null);
                                    setPaymentType('');
                                    setNumberOfInstallments(1);
                                    setModalDiscountAmount(0);
                                    setModalReferredPerson('');
                                    setModalReferralAmount(0);
                                }}
                                className="px-4 py-2 border border-gray-300 rounded-3xl hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={addNewRegistration}
                                disabled={!selectedFeeDetail || !paymentType}
                                className={`px-4 py-2 rounded-3xl text-white ${selectedFeeDetail && paymentType ? 'bg-gradient-to-r from-[#3483BE] to-[#02385E] hover:shadow-lg' : 'bg-gray-400 cursor-not-allowed'}`}
                            >
                                Add Registration
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Installment Management Modal */}
            {showInstallmentModal && selectedRegistrationForInstallments && selectedRegistrationForInstallments.installments && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-semibold">Manage Installments</h2>
                            <button
                                onClick={() => setShowInstallmentModal(false)}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                <X size={24} />
                            </button>
                        </div>

                        {/* Registration Summary */}
                        <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                            <h3 className="font-medium mb-2">Payment Summary:</h3>
                            <div className="grid grid-cols-2 gap-4 text-sm">
                                <p><strong>Registration:</strong> {selectedRegistrationForInstallments.feeDetailName}</p>
                                <p><strong>Total Amount:</strong> ₹{selectedRegistrationForInstallments.afterDiscountAmount}</p>

                                <p><strong>Previous Payments:</strong></p>
                                <p>₹{selectedRegistrationForInstallments.paidAmount}</p>

                                <p><strong>+ Installment Payments:</strong></p>
                                <p>₹{selectedRegistrationForInstallments.installments?.reduce((sum, inst) => sum + inst.paidAmount, 0) || 0}</p>

                                <p><strong>= Total Paid:</strong></p>
                                <p className="font-semibold text-green-600">
                                    ₹{selectedRegistrationForInstallments.paidAmount}
                                </p>

                                <p><strong>Balance:</strong></p>
                                <p className="font-semibold">
                                    ₹{selectedRegistrationForInstallments.pendingAmount}
                                </p>
                            </div>

                            {/* Full Payment Option */}
                            {selectedRegistrationForInstallments.pendingAmount > 0 && (
                                <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                    <h4 className="font-medium text-blue-800 mb-2">Full Payment Option</h4>
                                    <p className="text-sm text-blue-700 mb-3">
                                        Pay the remaining ₹{selectedRegistrationForInstallments.pendingAmount} in full and complete this registration.
                                    </p>
                                    <div className="flex space-x-2">
                                        {/* <button
                                            onClick={() => payFullRemainingAmount(selectedRegistrationForInstallments)}
                                            disabled={!write || processingFullPayment}
                                            className={`px-4 py-2 text-white rounded-md text-sm ${processingFullPayment
                                                ? 'bg-gray-400 cursor-not-allowed'
                                                : 'bg-green-600 hover:bg-green-700'
                                                }`}
                                        >
                                            {processingFullPayment ? (
                                                <div className="flex items-center space-x-2">
                                                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                                                    <span>Processing...</span>
                                                </div>
                                            ) : (
                                                'Pay Full Remaining Amount'
                                            )}
                                        </button> */}
                                        <button
                                            onClick={() => {
                                                setSelectedRegistrationForFullPayment(selectedRegistrationForInstallments);
                                                setShowFullPaymentModal(true);
                                            }}
                                            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm"
                                            disabled={!write || processingFullPayment}
                                        >
                                            Convert to Full Payment Plan
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>



                        {/* Installment List */}
                        <div className="space-y-4">
                            <h3 className="font-medium text-lg">Installments</h3>
                            {selectedRegistrationForInstallments.installments.map((installment, index) => (
                                <div key={index} className="border rounded-lg p-4">
                                    <div className="flex justify-between items-center mb-3">
                                        <h4 className="font-medium">
                                            Installment #{installment.installmentNumber}
                                        </h4>
                                        <span className={`px-2 py-1 rounded-full text-xs ${installment.status === 'paid' ? 'bg-green-100 text-green-800' :
                                            installment.status === 'partial' ? 'bg-yellow-100 text-yellow-800' :
                                                'bg-red-100 text-red-800'
                                            }`}>
                                            {installment.status}
                                        </span>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className={labelClass}>Due Date</label>
                                            <input
                                                type="date"
                                                value={installment.dueDate}
                                                onChange={(e) => updateInstallment(
                                                    selectedRegistrationForInstallments.id,
                                                    index,
                                                    'dueDate',
                                                    e.target.value
                                                )}
                                                className={inputClass}
                                                disabled={!write}
                                            />
                                        </div>

                                        <div>
                                            <label className={labelClass}>Installment Amount (₹)</label>
                                            <input
                                                type="number"
                                                min="0"
                                                value={installment.installmentAmount === 0 ? '' : installment.installmentAmount}
                                                onChange={(e) => {
                                                    const newAmount = Number(e.target.value) || 0;
                                                    redistributeInstallmentAmounts(
                                                        selectedRegistrationForInstallments.id,
                                                        newAmount,
                                                        index
                                                    );
                                                }}
                                                className={inputClass}
                                                disabled={write}
                                            />
                                        </div>

                                        <div className="mt-3">
                                            <label className={labelClass}>Paid Amount (₹)</label>
                                            <input
                                                type="number"
                                                min="0"
                                                max={installment.installmentAmount * 2} // Allow some overpayment
                                                value={installment.paidAmount === 0 ? '' : installment.paidAmount}
                                                onChange={(e) => {
                                                    const newPaidAmount = Number(e.target.value) || 0;
                                                    updateInstallment(
                                                        selectedRegistrationForInstallments.id,
                                                        index,
                                                        'paidAmount',
                                                        newPaidAmount
                                                    );
                                                }}
                                                className={inputClass}
                                                disabled={!write}
                                            />

                                            {/* Show redistribution info */}
                                            {/* {getRedistributionInfo(selectedRegistrationForInstallments, index, installment.paidAmount)} */}
                                        </div>

                                        <div>
                                            <label className={labelClass}>Upload Receipt</label>
                                            <div className="flex items-center">
                                                <input
                                                    type="file"
                                                    id={`installment-receipt-${index}`}
                                                    className="hidden"
                                                    disabled={!write}
                                                    onChange={async (e) => {
                                                        const file = e.target.files?.[0];
                                                        if (file) {
                                                            const downloadURL = await handleFileUpload(selectedRegistrationForInstallments.id, file);
                                                            if (downloadURL) {
                                                                updateInstallment(
                                                                    selectedRegistrationForInstallments.id,
                                                                    index,
                                                                    'receiptUrl',
                                                                    downloadURL
                                                                );
                                                            }
                                                        }
                                                    }}
                                                />
                                                <label
                                                    htmlFor={`installment-receipt-${index}`}
                                                    className={`px-3 py-2 bg-gray-100 rounded-l-md border border-gray-300 text-sm ${!write ? 'cursor-not-allowed' : 'cursor-pointer hover:bg-gray-200'}`}
                                                >
                                                    Choose File
                                                </label>
                                                <input
                                                    type="text"
                                                    value={installment.receiptUrl ? 'Receipt uploaded' : 'No receipt'}
                                                    className="flex-1 py-2 px-3 border border-gray-300 border-l-0 rounded-r-md bg-white text-sm"
                                                    readOnly
                                                />
                                            </div>
                                            {installment.receiptUrl && (
                                                <a
                                                    href={installment.receiptUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-blue-600 text-sm mt-1 inline-block"
                                                >
                                                    View receipt
                                                </a>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Modal Footer */}
                        <div className="flex justify-end space-x-3 mt-6 pt-4 border-t">
                            <button
                                onClick={() => setShowInstallmentModal(false)}
                                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                            >
                                Close
                            </button>
                            {write && (
                                <button
                                    onClick={() => {
                                        if (selectedRegistrationForInstallments) {
                                            saveRegistrationToFirestore(selectedRegistrationForInstallments);
                                        }
                                        setShowInstallmentModal(false);
                                    }}
                                    className="px-4 py-2 bg-gradient-to-r from-[#3483BE] to-[#02385E] text-white rounded-md hover:shadow-lg"
                                >
                                    Save Installments
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {showFullPaymentModal && selectedRegistrationForFullPayment && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-md">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-semibold text-blue-600">Convert to Full Payment</h2>
                            <button
                                onClick={() => {
                                    setShowFullPaymentModal(false);
                                    setSelectedRegistrationForFullPayment(null);
                                }}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                <X size={24} />
                            </button>
                        </div>

                        <div className="mb-6">
                            <p className="text-gray-700 mb-4">
                                Are you sure you want to convert this installment plan to a full payment plan?
                            </p>

                            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                                <h4 className="font-medium text-yellow-800 mb-2">What will happen:</h4>
                                <ul className="text-sm text-yellow-700 list-disc list-inside space-y-1">
                                    <li>All remaining installments will be combined into a single payment</li>
                                    <li>Installment schedule will be removed</li>
                                    <li>You can pay the remaining amount in one transaction</li>
                                    <li>Payment status will be updated accordingly</li>
                                </ul>
                            </div>

                            <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                                <p className="text-sm">
                                    <strong>Remaining Balance:</strong> ₹{selectedRegistrationForFullPayment.pendingAmount}
                                </p>
                                <p className="text-sm">
                                    <strong>Registration:</strong> {selectedRegistrationForFullPayment.feeDetailName}
                                </p>
                            </div>
                        </div>

                        <div className="flex justify-end space-x-3">
                            <button
                                onClick={() => {
                                    setShowFullPaymentModal(false);
                                    setSelectedRegistrationForFullPayment(null);
                                }}
                                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => convertToFullPayment(selectedRegistrationForFullPayment)}
                                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                            >
                                Convert to Full Payment
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {deleteConfirmation.show && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-md">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-semibold text-red-600">Confirm Delete</h2>
                            <button
                                onClick={cancelDelete}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                <X size={24} />
                            </button>
                        </div>

                        <div className="mb-6">
                            <p className="text-gray-700">
                                Are you sure you want to delete this registration? This action cannot be undone.
                            </p>
                            {deleteConfirmation.registrationId && (
                                <div className="mt-3 p-3 bg-red-50 rounded-lg">
                                    <p className="text-sm text-red-800">
                                        <strong>Registration:</strong> {
                                            registrations.find(r => r.id === deleteConfirmation.registrationId)?.feeDetailName ||
                                            `Registration #${deleteConfirmation.registrationId}`
                                        }
                                    </p>
                                </div>
                            )}
                        </div>

                        <div className="flex justify-end space-x-3">
                            <button
                                onClick={cancelDelete}
                                className="px-4 py-2 border border-gray-300 rounded-3xl hover:bg-gray-50 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => deleteConfirmation.registrationId && deleteRegistration(deleteConfirmation.registrationId)}
                                className="px-4 py-2 bg-red-600 text-white rounded-3xl hover:bg-red-700 transition-colors"
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}


            {/* Convert to Installment Modal after the other modals */}
            {showConvertToInstallmentModal && registrationForInstallmentConversion && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-md">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-semibold text-blue-600">Convert to Installment Plan</h2>
                            <button
                                onClick={() => {
                                    setShowConvertToInstallmentModal(false);
                                    setRegistrationForInstallmentConversion(null);
                                    setNewNumberOfInstallments(1);
                                }}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                <X size={24} />
                            </button>
                        </div>

                        <div className="mb-6">
                            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg mb-4">
                                <h4 className="font-medium text-blue-800 mb-2">Convert Payment Plan</h4>
                                <p className="text-sm text-blue-700">
                                    Convert this full payment registration to an installment plan.
                                </p>
                            </div>

                            <div className="p-4 bg-gray-50 rounded-lg mb-4">
                                <p className="text-sm font-medium mb-2">Payment Summary:</p>
                                <div className="grid grid-cols-2 gap-2 text-sm">
                                    <p><strong>Registration:</strong></p>
                                    <p>{registrationForInstallmentConversion.feeDetailName}</p>

                                    <p><strong>Total Amount:</strong></p>
                                    <p>₹{registrationForInstallmentConversion.afterDiscountAmount}</p>

                                    <p><strong>Already Paid:</strong></p>
                                    <p>₹{registrationForInstallmentConversion.paidAmount}</p>

                                    <p><strong>Remaining Amount:</strong></p>
                                    <p className="font-semibold">₹{registrationForInstallmentConversion.pendingAmount}</p>
                                </div>
                            </div>

                            <div className="mb-4">
                                <label className={labelClass}>Number of Installments</label>
                                <div className="relative">
                                    <select
                                        value={newNumberOfInstallments}
                                        onChange={(e) => setNewNumberOfInstallments(Number(e.target.value))}
                                        className={`${inputClass} appearance-none`}
                                    >
                                        {Array.from({ length: 12 }, (_, i) => i + 1).map((num) => (
                                            <option key={num} value={num}>
                                                {num} {num === 1 ? 'installment' : 'installments'}
                                            </option>
                                        ))}
                                    </select>
                                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                                        <ChevronDown size={20} />
                                    </div>
                                </div>
                                <p className="text-xs text-gray-500 mt-1">
                                    The remaining amount of ₹{registrationForInstallmentConversion.pendingAmount} will be split into {newNumberOfInstallments} installments.
                                </p>
                            </div>

                            {newNumberOfInstallments > 0 && (
                                <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                                    <p className="text-sm text-green-700 font-medium mb-1">Installment Breakdown:</p>
                                    <p className="text-sm text-green-700 mb-2">
                                        <strong>Already Paid:</strong> ₹{registrationForInstallmentConversion.paidAmount}
                                    </p>
                                    <p className="text-sm text-green-700 mb-2">
                                        <strong>Remaining to pay in {newNumberOfInstallments} installments:</strong> ₹{registrationForInstallmentConversion.pendingAmount}
                                    </p>
                                    {Array.from({ length: newNumberOfInstallments }, (_, i) => {
                                        const installmentAmount = Math.round(registrationForInstallmentConversion.pendingAmount / newNumberOfInstallments);
                                        const remainder = registrationForInstallmentConversion.pendingAmount - (installmentAmount * (newNumberOfInstallments - 1));
                                        const amount = i === newNumberOfInstallments - 1 ? remainder : installmentAmount;

                                        return (
                                            <p key={i} className="text-sm text-green-700">
                                                Installment {i + 1}: ₹{amount}
                                            </p>
                                        );
                                    })}
                                </div>
                            )}

                            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg mt-4">
                                <p className="text-sm text-yellow-700">
                                    <strong>Note:</strong> This will convert the payment plan to installments while keeping the existing paid amount.
                                </p>
                            </div>
                        </div>

                        <div className="flex justify-end space-x-3">
                            <button
                                onClick={() => {
                                    setShowConvertToInstallmentModal(false);
                                    setRegistrationForInstallmentConversion(null);
                                    setNewNumberOfInstallments(1);
                                }}
                                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => convertToInstallment(registrationForInstallmentConversion)}
                                disabled={processingFullPayment}
                                className={`px-4 py-2 text-white rounded-md ${processingFullPayment
                                    ? 'bg-gray-400 cursor-not-allowed'
                                    : 'bg-blue-600 hover:bg-blue-700'
                                    }`}
                            >
                                {processingFullPayment ? (
                                    <div className="flex items-center space-x-2">
                                        <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                                        <span>Converting...</span>
                                    </div>
                                ) : (
                                    'Convert to Installment'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Receipt Upload Modal after the other modals */}
            {/* {showReceiptUploadModal && registrationAfterFullPayment && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-md">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-semibold text-green-600">Upload Full Payment Receipt</h2>
                            <button
                                onClick={handleSkipReceiptUpload}
                                className="text-gray-500 hover:text-gray-700"
                                disabled={processingFullPayment}
                            >
                                <X size={24} />
                            </button>
                        </div>

                        <div className="mb-6">
                            <div className="p-4 bg-green-50 border border-green-200 rounded-lg mb-4">
                                <h4 className="font-medium text-green-800 mb-2">Full Payment Processed Successfully!</h4>
                                <p className="text-sm text-green-700">
                                    All installments have been marked as paid. Please upload the receipt for the <strong>full payment</strong>.
                                </p>
                            </div>

                            <div className="p-4 bg-gray-50 rounded-lg mb-4">
                                <p className="text-sm font-medium mb-2">Payment Summary:</p>
                                <div className="grid grid-cols-2 gap-2 text-sm">
                                    <p><strong>Registration:</strong></p>
                                    <p>{registrationAfterFullPayment.feeDetailName}</p>

                                    <p><strong>Total Fee:</strong></p>
                                    <p>₹{registrationAfterFullPayment.registrationFee}</p>

                                    {registrationAfterFullPayment.discountAmount > 0 && (
                                        <>
                                            <p><strong>Discount:</strong></p>
                                            <p>₹{registrationAfterFullPayment.discountAmount}</p>
                                        </>
                                    )}

                                    <p><strong>Final Amount:</strong></p>
                                    <p className="font-semibold">₹{registrationAfterFullPayment.afterDiscountAmount}</p>

                                    <p><strong>Total Paid:</strong></p>
                                    <p className="text-green-600 font-medium">₹{registrationAfterFullPayment.paidAmount}</p>

                                    <p><strong>Status:</strong></p>
                                    <p className="text-green-600 font-medium">Fully Paid</p>
                                </div>
                            </div>

                            <div className="mb-4">
                                <label className={`${labelClass} mb-2`}>Upload Full Payment Receipt</label>
                                <div className="flex items-center">
                                    <input
                                        type="file"
                                        id="full-payment-receipt"
                                        className="hidden"
                                        disabled={processingFullPayment}
                                        onChange={(e) => {
                                            const file = e.target.files?.[0];
                                            if (file) {
                                                setFullPaymentReceiptFile(file);
                                            }
                                        }}
                                        accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                                    />
                                    <label
                                        htmlFor="full-payment-receipt"
                                        className={`px-4 py-2 bg-gray-100 rounded-l-3xl border border-gray-300 ${processingFullPayment ? 'cursor-not-allowed' : 'cursor-pointer hover:bg-gray-200'
                                            }`}
                                    >
                                        Choose File
                                    </label>
                                    <input
                                        type="text"
                                        value={fullPaymentReceiptFile ? fullPaymentReceiptFile.name : 'No file chosen'}
                                        className="flex-1 py-2 px-4 border border-gray-300 border-l-0 rounded-r-3xl bg-white text-sm"
                                        readOnly
                                    />
                                </div>
                                <p className="text-xs text-gray-500 mt-1">
                                    Upload receipt for the full payment of <strong>₹{registrationAfterFullPayment.afterDiscountAmount}</strong>
                                </p>
                            </div>

                            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                <p className="text-sm text-blue-700">
                                    <strong>Note:</strong> This receipt should cover the complete payment of <strong>₹{registrationAfterFullPayment.afterDiscountAmount}</strong> including all installments.
                                </p>
                            </div>
                        </div>

                        <div className="flex justify-end space-x-3">
                            <button
                                onClick={handleSkipReceiptUpload}
                                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                                disabled={processingFullPayment}
                            >
                                {processingFullPayment ? 'Saving...' : 'Skip Receipt'}
                            </button>
                            <button
                                onClick={handleFullPaymentReceiptUpload}
                                disabled={!fullPaymentReceiptFile || processingFullPayment}
                                className={`px-4 py-2 text-white rounded-md ${!fullPaymentReceiptFile || processingFullPayment
                                    ? 'bg-gray-400 cursor-not-allowed'
                                    : 'bg-green-600 hover:bg-green-700'
                                    }`}
                            >
                                {processingFullPayment ? (
                                    <div className="flex items-center space-x-2">
                                        <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                                        <span>Saving...</span>
                                    </div>
                                ) : (
                                    'Upload & Save All Data'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )} */}
        </div>
    );
};

export default MultiRegistrations;